import React from "react";
import { Link } from "react-router-dom";
import { Target, Eye, Heart, ArrowRight, Quote, Award, Users, GraduationCap, Sparkles, Crown } from "lucide-react";
import { collegeInfo, boardMembers } from "../mock/mock";

const PageHero = ({ eyebrow, title, subtitle, image }) => (
  <section className="relative h-[45vh] min-h-[340px] w-full overflow-hidden bg-brand-green-deep">
    <img src={image} alt={title} className="absolute inset-0 w-full h-full object-cover opacity-40 ken-burns" />
    <div className="absolute inset-0 bg-gradient-to-b from-brand-green-deep/70 via-brand-green-deep/60 to-brand-green-deep" />
    <div className="relative z-10 max-w-7xl mx-auto px-6 h-full flex flex-col justify-end pb-14">
      <div className="divider-ornament text-xs uppercase tracking-[0.3em] mb-3" style={{ color: "#d4af37" }}>{eyebrow}</div>
      <h1 className="font-serif-display text-5xl md:text-6xl font-bold" style={{ color: "#f4efe4" }}>{title}</h1>
      {subtitle && <p className="mt-3 text-lg max-w-2xl" style={{ color: "rgba(244, 239, 228, 0.9)" }}>{subtitle}</p>}
    </div>
  </section>
);

const About = () => {
  return (
    <div>
      <PageHero
        eyebrow="About Us"
        title="A legacy of teacher education"
        subtitle="Since 2008, shaping educators who inspire generations of learners."
        image="https://images.unsplash.com/photo-1509062522246-3755977927d7"
      />

      {/* Intro */}
      <section className="section-y bg-white">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-5 gap-12 items-start">
          <div className="lg:col-span-2">
            <div className="divider-ornament text-xs uppercase tracking-[0.3em] mb-4">Our Story</div>
            <h2 className="font-serif-display text-4xl font-bold text-brand-green-deep leading-tight">
              Where sophistication meets <span className="text-brand-gold-dark italic">academic excellence.</span>
            </h2>
          </div>
          <div className="lg:col-span-3 space-y-5 text-neutral-700 leading-relaxed">
            <p>
              Step into the prestigious halls of SHES D.Ed College &mdash; where excellence meets opulence. We take
              immense pride in nurturing the future educators of tomorrow through unparalleled quality education and immersive
              hands-on training.
            </p>
            <p>
              Successful and happy individuals build successful nations. Achievement of goals and the fulfillment of one&rsquo;s
              mission requires one strong foundation &mdash; good education. In education lies the biggest service to the individual,
              the nation, and to humanity.
            </p>
            <p>
              With students fired with determination to excel, competent and dedicated faculty, meticulously designed curricula,
              and optimal infrastructure, SHES D.Ed College is emerging as a reckoning force in the world of teacher education.
            </p>
          </div>
        </div>
      </section>

      {/* Vision, Mission, Values */}
      <section className="section-y bg-brand-ivory">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="divider-ornament text-xs uppercase tracking-[0.3em] mb-3">What guides us</div>
            <h2 className="font-serif-display text-4xl md:text-5xl font-bold text-brand-green-deep">
              Vision, Mission &amp; Values
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Eye,
                title: "Our Vision",
                text: "To be a nationally recognized institution setting benchmarks in teacher education by preparing educators who lead classrooms with confidence, compassion, and creativity.",
              },
              {
                icon: Target,
                title: "Our Mission",
                text: "To deliver holistic, research-informed teacher training programs that blend academic rigor with real-world classroom practice, technology, and reflective pedagogy.",
              },
              {
                icon: Heart,
                title: "Our Values",
                text: "Integrity, inclusivity, innovation, and a deep respect for the profession of teaching guide every classroom, decision, and interaction at our college.",
              },
            ].map((v, i) => (
              <div
                key={i}
                className="bg-white p-8 rounded-sm border border-brand-gold/25 hover-lift animate-fade-up"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div className="w-14 h-14 rounded-full bg-brand-green flex items-center justify-center border-2 border-brand-gold mb-5">
                  <v.icon className="w-6 h-6 text-brand-gold" />
                </div>
                <h3 className="font-serif-display text-2xl text-brand-green-deep mb-3">{v.title}</h3>
                <p className="text-neutral-600 leading-relaxed text-sm">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* President Message */}
      <section className="section-y bg-white">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="absolute -top-4 -left-4 w-24 h-24 border-2 border-brand-gold rounded-sm hidden md:block" />
            <img
              src="https://images.unsplash.com/photo-1607013407627-6ee814329547"
              alt="President"
              className="relative rounded-sm w-full h-[440px] object-cover shadow-xl"
            />
          </div>
          <div className="order-1 lg:order-2">
            <div className="divider-ornament text-xs uppercase tracking-[0.3em] mb-3">Message</div>
            <h2 className="font-serif-display text-4xl font-bold text-brand-green-deep mb-6">From the President&rsquo;s Desk</h2>
            <Quote className="w-10 h-10 text-brand-gold/40 mb-4" />
            <p className="font-serif-body text-xl text-neutral-700 leading-relaxed mb-4">
              The young and promising students of our country are most welcome to our institute. We have dedicated ourselves to
              carve their career in such a befitting manner so as to enable them to successfully cope with the emerging
              challenges of life and profession.
            </p>
            <p className="text-neutral-600 leading-relaxed mb-6">
              At SHES D.Ed College, we not only inculcate the best creative and technical qualifications but also the
              indispensable human qualities that define true educators.
            </p>
            <div className="pt-5 border-t border-brand-gold/30">
              <div className="font-serif-display text-xl text-brand-green-deep font-bold">Shri. Anonymous Ji</div>
              <div className="text-xs uppercase tracking-widest text-brand-gold-dark">President, Educational Society</div>
            </div>
          </div>
        </div>
      </section>

      {/* Principal Message */}
      <section className="section-y bg-brand-green-deep text-brand-ivory relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(212,175,55,0.15),transparent_55%)]" />
        <div className="relative max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="divider-ornament text-xs uppercase tracking-[0.3em] mb-3 text-brand-gold">Principal&rsquo;s Message</div>
            <h2 className="font-serif-display text-4xl font-bold mb-6">Education is the biggest service</h2>
            <p className="font-serif-body text-xl leading-relaxed mb-4 text-brand-ivory/90">
              &ldquo;Our target is to equip each and every student with the best education and infrastructure to help them achieve
              nothing but the best in life. We inculcate creative, technical and indispensable human qualities.&rdquo;
            </p>
            <div className="pt-5 border-t border-brand-gold/30 mt-6">
              <div className="font-serif-display text-xl text-brand-gold font-bold">Dr. Prakash M. Deshpande</div>
              <div className="text-xs uppercase tracking-widest text-brand-ivory/70">Principal</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: Award, label: "NCTE Approved", stat: "100%" },
              { icon: Users, label: "Alumni", stat: "1500+" },
              { icon: GraduationCap, label: "Legacy", stat: "16 Yrs" },
              { icon: Sparkles, label: "Results", stat: "100%" },
            ].map((s, i) => (
              <div key={i} className="bg-brand-green/40 border border-brand-gold/25 backdrop-blur p-6 rounded-sm">
                <s.icon className="w-7 h-7 text-brand-gold mb-3" />
                <div className="font-serif-display text-3xl font-bold text-brand-ivory">{s.stat}</div>
                <div className="text-xs uppercase tracking-widest text-brand-ivory/70 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Accreditations */}
      <section className="section-y bg-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="divider-ornament text-xs uppercase tracking-[0.3em] mb-3">Accreditations</div>
          <h2 className="font-serif-display text-4xl font-bold text-brand-green-deep mb-10">Approved &amp; Recognized</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {collegeInfo.affiliations.map((a, i) => (
              <div key={i} className="bg-brand-ivory rounded-sm p-8 border border-brand-gold/25 hover-lift">
                <Award className="w-10 h-10 text-brand-gold mx-auto mb-4" />
                <div className="font-serif-display text-lg text-brand-green-deep font-semibold">{a}</div>
              </div>
            ))}
          </div>
          <Link to="/admissions" className="mt-10 inline-flex items-center gap-2 btn-primary px-7 py-3 rounded-full text-sm font-semibold">
            Explore Admissions <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Board Members */}
      <BoardMembersSection />
    </div>
  );
};

const MemberCard = ({ person, size = "md", highlight = false }) => {
  const isLg = size === "lg";
  return (
    <div className="group flex flex-col items-center animate-fade-up">
      <div
        className={`relative rounded-sm overflow-hidden shadow-lg transition-transform duration-500 group-hover:-translate-y-1 ${
          isLg ? "w-44 h-44 md:w-52 md:h-52" : "w-32 h-32 md:w-36 md:h-36"
        }`}
      >
        <img src={person.image} alt={person.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 ring-1 ring-inset ring-brand-gold/40 pointer-events-none" />
        {highlight && (
          <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-brand-gold flex items-center justify-center shadow-lg">
            <Crown className="w-4 h-4 text-brand-green-deep" />
          </div>
        )}
      </div>
      <div
        className={`mt-[-14px] relative z-10 rounded-sm shadow-md text-center px-4 py-3 min-w-[180px] ${
          highlight
            ? "bg-brand-gold border border-brand-gold-dark"
            : "bg-brand-green-deep border border-brand-gold/40"
        }`}
      >
        <div
          className={`font-serif-display font-bold leading-tight ${isLg ? "text-base md:text-lg" : "text-sm md:text-[15px]"}`}
          style={{ color: highlight ? "#0f3d29" : "#f4efe4" }}
        >
          {person.name}
        </div>
        <div
          className="text-[10px] mt-1 uppercase tracking-[0.15em] font-semibold"
          style={{ color: highlight ? "rgba(15, 61, 41, 0.8)" : "#d4af37" }}
        >
          {person.role}
        </div>
      </div>
    </div>
  );
};

const BoardMembersSection = () => (
  <section className="section-y bg-brand-ivory relative overflow-hidden">
    <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: "radial-gradient(#1b5e3f 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
    <div className="relative max-w-7xl mx-auto px-6">
      <div className="text-center mb-14">
        <div className="divider-ornament text-xs uppercase tracking-[0.3em] mb-3">Governing Body</div>
        <h2 className="font-serif-display text-4xl md:text-5xl font-bold text-brand-green-deep leading-tight max-w-3xl mx-auto">
          Our prestigious board members, who plan and support the smooth functioning of <span className="text-brand-gold-dark italic">SHES Society</span>
        </h2>
      </div>

      {/* President */}
      <div className="flex justify-center mb-12">
        <MemberCard person={boardMembers.president} size="lg" highlight />
      </div>

      {/* Executives */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10 mb-16 max-w-5xl mx-auto">
        {boardMembers.executives.map((e) => (
          <MemberCard key={e.name} person={e} />
        ))}
      </div>

      {/* Divider */}
      <div className="flex items-center justify-center gap-4 mb-10">
        <span className="h-px w-16 bg-brand-gold/50" />
        <h3 className="font-serif-display text-2xl md:text-3xl text-brand-green-deep font-bold">M. C. Members</h3>
        <span className="h-px w-16 bg-brand-gold/50" />
      </div>

      {/* Members grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 md:gap-8">
        {boardMembers.members.map((m) => (
          <MemberCard key={m.name} person={m} />
        ))}
      </div>
    </div>
  </section>
);

export default About;
export { PageHero };
